import React, { useCallback, useEffect, useState } from 'react';
import dayjs from 'dayjs';
import {
  SandboxShipmentStatus,
  WebhookDeliveryStatus,
} from '@/services/api/sandbox';
import { getShipmentStatusLabel } from '@/pages/shipments/ShipmentStatusBadge';

// Shared bits of the Sandbox list and detail. Status colours come from the
// Shipments page's ShipmentStatusBadge — a sandbox shipment must read exactly
// like a live one.

/**
 * A button that rests after a click (against hammering it): `left` is the
 * seconds still to wait, 0 = ready; `start()` begins the rest.
 */
export const useCooldown = (seconds: number) => {
  const [left, setLeft] = useState(0);
  useEffect(() => {
    if (left <= 0) return undefined;
    const t = setTimeout(() => setLeft((n) => n - 1), 1000);
    return () => clearTimeout(t);
  }, [left]);
  const start = useCallback(() => setLeft(seconds), [seconds]);
  return { left, start };
};

/**
 * Classes for a button while it rests: the countdown on it is what the user
 * reads, so it keeps a border and legible text instead of the faint disabled
 * look (which reads as the button having gone).
 */
export const RESTING_BUTTON =
  'border border-stroke-sub-300 bg-bg-white-0! text-text-sub-600! tabular-nums';

export const formatTime = (iso: string | null | undefined) =>
  iso ? dayjs(iso).format('DD MMM YYYY, HH:mm:ss') : '—';

/** A date-only `YYYY-MM-DD` in the same style as `formatTime`. */
export const formatDate = (date: string | null | undefined) =>
  date ? dayjs(date).format('DD MMM YYYY') : '—';

/** What a simulator button does, in the words of the step it takes. */
export const STEP_LABEL: Partial<Record<SandboxShipmentStatus, string>> = {
  AT_HUB: 'Arrive at hub',
  DISPATCHED: 'Dispatch to rider',
  IN_DELIVERY: 'Out for delivery',
  COMPLETED: 'Complete delivery',
  FAILED: 'Fail delivery',
  RETURNED: 'Return to sender',
};

/** The happy path the stepper draws; branches are shown on the step they
 *  leave from. */
export const HAPPY_PATH: SandboxShipmentStatus[] = [
  'CREATED',
  'AT_HUB',
  'DISPATCHED',
  'IN_DELIVERY',
  'COMPLETED',
];

export const statusLabel = (status: string) => getShipmentStatusLabel(status);

const DELIVERY_META: Record<
  WebhookDeliveryStatus,
  { label: string; tone: string }
> = {
  PENDING: { label: 'Queued', tone: 'bg-bg-weak-50 text-text-sub-600' },
  PROCESSING: {
    label: 'Sending',
    tone: 'bg-information-lighter text-information-dark',
  },
  DONE: { label: 'Delivered', tone: 'bg-success-lighter text-success-dark' },
  FAILED: { label: 'Failed', tone: 'bg-warning-lighter text-warning-dark' },
  // Out of automatic retries: still a failure to the partner — the rows say
  // that only a manual retry can send it now.
  DEAD: { label: 'Failed', tone: 'bg-warning-lighter text-warning-dark' },
  SKIPPED: { label: 'Skipped', tone: 'bg-faded-lighter text-faded-dark' },
};

export const DeliveryStatusPill: React.FC<{
  status: WebhookDeliveryStatus;
}> = ({ status }) => {
  const meta = DELIVERY_META[status] ?? DELIVERY_META.PENDING;
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-medium ${meta.tone}`}
    >
      <span className="h-1.5 w-1.5 rounded-full bg-current opacity-70" />
      {meta.label}
    </span>
  );
};

/** Pretty JSON on the dark code surface, with a copy button. */
export const JsonBlock: React.FC<{
  value: unknown;
  label?: string;
  maxHeight?: string;
  /** Wrap long lines instead of scrolling sideways (short, wordy bodies). */
  wrap?: boolean;
}> = ({ value, label, maxHeight = 'max-h-96', wrap = false }) => {
  const [copied, setCopied] = useState(false);
  const text =
    typeof value === 'string' ? value : JSON.stringify(value, null, 2);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // Clipboard can be unavailable (permissions) — the text stays selectable.
    }
  };
  return (
    <div className="overflow-hidden rounded-lg bg-bg-strong-950">
      <div className="flex items-center justify-between gap-3 border-b border-static-white/10 px-4 py-2">
        <span className="text-xs font-medium text-static-white/70">
          {label ?? 'JSON'}
        </span>
        <button
          type="button"
          onClick={copy}
          className="text-xs font-medium text-static-white/80 hover:text-static-white"
        >
          <i className="ri-file-copy-line mr-1" />
          {copied ? 'Copied' : 'Copy'}
        </button>
      </div>
      <pre
        className={`${maxHeight} overflow-auto px-4 py-3 font-mono text-xs leading-relaxed text-static-white ${
          wrap ? 'whitespace-pre-wrap break-words' : ''
        }`}
      >
        {text}
      </pre>
    </div>
  );
};
